import React, {
  useEffect,
  useState,
  useContext,
  useImperativeHandle,
  forwardRef,
} from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Button,
  Grid2,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Fab,
  Skeleton,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Divider,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Edit from "@mui/icons-material/Edit";
import Tooltip from "@mui/material/Tooltip";
import { LoadingButton } from "@mui/lab";
import { getPatients, searchPatients } from "../API/PatientsApi";
import { useSnackbar } from "../Utility/UserMessageContext";
import authContext from "../Context/AuthProvider";
import { AddRounded, SearchRounded } from "@mui/icons-material";
import dayjs from "dayjs";
import NewPatient from "./NewPatient";
import { capitalizeFirstLetter, formatAge } from "../Utility/Utility";
import duration from "dayjs/plugin/duration";
import PatientSearchDialog from "./PatientSearchDialog";
import MenuBar from "../common/MenuBar";
import MoreVert from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";

const PatientList = forwardRef(
  ({ onViewDetails, onNewPatient, onEditPatient }, ref) => {
    const [patients, setPatients] = useState([]);
    const { showSnackbar } = useSnackbar();
    const { auth } = useContext(authContext);
    const [showSpinner, setShowSpinner] = useState(false);
    const [showSearchPanel, setShowSearchPanel] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [menuAnchor, setMenuAnchor] = useState(null);
const [currentPatient, setCurrentPatient] = useState(null);
    const handleMenuOpen = (event, patient) => {
  setMenuAnchor(event.currentTarget);
  setCurrentPatient(patient);
};

const handleMenuClose = () => {
  setMenuAnchor(null);
  setCurrentPatient(null);
};

    useEffect(() => {
      const loadPatients = async () => {
        setShowSpinner(true);
        const result = await getPatients(auth);
        if (result.isSuccess) {
          setPatients(result.data);
        } else {
          showSnackbar("Failed to load patients", "error");
        }
        setShowSpinner(false);
      };

      loadPatients();
    }, [auth]);

    // const searchPatient = async (e) => {
    //   e.preventDefault();

    //   if (searchKeyword.trim().length < 3) {
    //     showSnackbar(
    //       "Enter at least 3 characters to search. Special characters are not allowed.",
    //       "error"
    //     );
    //   } else {
    //     try {
    //       const result = await searchPatients(auth, searchKeyword);
    //       if (result?.isSuccess) {
    //         setSelectedPatientId(null);
    //         setSearchResult(result?.data);
    //       } else {
    //         setSearchResult([]);
    //       }
    //     } catch (error) {
    //       console.error("Error searching patients:", error);
    //       setSearchResult([]);
    //     }
    //   }
    // };

    const refreshPatientList = async () => {
      setShowSpinner(true);
      const result = await getPatients(auth);
      if (result.isSuccess) {
        setPatients(result.data);
      } else {
        showSnackbar("Failed to load patients", "error");
      }
      setShowSpinner(false);
    };

    useImperativeHandle(ref, () => ({
      refreshPatientList,
    }));

    const patientColumns = [
   {
  field: "mrn",
  headerName: "MRN",
  width: 260,
  renderCell: (params) => (
    <Box sx={{ 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "space-between",
      width: "100%",
      pr: 1 // Add some padding right
    }}>
      <span
        style={{
          textDecoration: "underline",
          cursor: "pointer",
          color: "primary.main",
        }}
        onClick={() => onViewDetails(params.row)}
      >
        {params.row.mrn}
      </span>
      
      <Tooltip title="Actions" arrow>
        <IconButton
          size="small"
          onClick={(event) => handleMenuOpen(event, params.row)}
          sx={{ ml: 'auto' }} // Push to the right
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  ),
},
{
  field: "patient",
  type: "text",
  headerName: "Patient",
  width: 180,
  renderCell: (params) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {`${capitalizeFirstLetter(
        params?.row?.firstName
      )} ${capitalizeFirstLetter(params?.row?.lastName)}`}
    </Box>
  ),
},
      { field: "gender", headerName: "Gender", width: 100 },
      {
        field: "ageInYears",
        headerName: "Age",
        width: 160,
        renderCell: (params) => `${formatAge(params?.row?.ageInYears)}`,
      },
      { field: "mobileNo", headerName: "Phone No.", width: 150 },
      { field: "emailId", headerName: "Email Id", width: 200 },
      // {
      //   field: "actions",
      //   headerName: "Actions",
      //   width: 100,
      //   headerClassName: "sticky-column",
      //   cellClassName: "sticky-column",
      //   renderCell: (params) => {
      //     if (!params || !params.row) return null;
      //     return (
      //       <>
      //         <Tooltip title="View Details" arrow>
      //           <IconButton onClick={() => onViewDetails(params.row)}>
      //             <VisibilityIcon fontSize="small" />
      //           </IconButton>
      //         </Tooltip>
      //         <Tooltip title="Edit Patient" arrow>
      //           <IconButton onClick={() => onEditPatient(params.row)}>
      //             <Edit fontSize="small" />
      //           </IconButton>
      //         </Tooltip>
      //       </>
      //     );
      //   },
      // },
    ];

    const renderPatientCards = () => (
      <Box sx={{ px: 1 }}>
        {patients.map((patient) => (
          <Card
            key={patient.id}
            variant="outlined"
            sx={{ mb: 2, borderRadius: 2 }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight="bold">
                {capitalizeFirstLetter(patient.firstName)}{" "}
                {capitalizeFirstLetter(patient.lastName)}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" gutterBottom>
                MRN: {patient.mrn}
              </Typography>
              <Typography variant="body2">Gender: {patient.gender}</Typography>
              <Typography variant="body2">
                Age: {formatAge(patient.ageInYears)}
              </Typography>
              <Typography variant="body2">Phone: {patient.mobileNo}</Typography>
              <Typography variant="body2">Email: {patient.emailId}</Typography>
            </CardContent>

            <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => onViewDetails(patient)}
              >
                View
              </Button>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<Edit />}
                onClick={() => onEditPatient(patient)}
              >
                Edit
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>
    );

    return (
      <>
        <Grid2 container size={12}>
          {/* Fixed Header Section */}
          <Grid2 container size={12}>
            <Grid2 size={12}>
              <MenuBar
                showNew={true}
                onNew={onNewPatient}
                newLabel="New Patient"
                newTooltip="New Patient"
                showSearchPatient={true}
                onSearchPatient={setShowSearchPanel}
                disabled={false}
              />
            </Grid2>
            {/* Heading Display */}
            <Grid2 size={12} sx={{ px: 1, py: 1 }}>
              <Typography variant="body1" sx={{ ml: 0 }}>
                Recent Patients (Upto 100)
              </Typography>
            </Grid2>
          </Grid2>

          {/* Scrollable Content Section */}
          {isMobile ? (
            <Grid2
              container
              size={12}
              sx={{
                maxHeight: "calc(100vh - 170px)",
                overflowY: "auto",
                width: "100%",
              }}
            >
              {renderPatientCards()}
            </Grid2>
          ) : (
            <Grid2
              container
              size={12}
              sx={{
                maxHeight: "calc(100vh - 170px)",
                overflowY: "auto",
                paddingX: "5px",
                height: 600,
              }}
            >
              <DataGrid
                localeText={{
                  noRowsLabel: "There are no patients to display.",
                }}
                loading={showSpinner}
                slotProps={{
                  loadingOverlay: {
                    variant: "skeleton",
                    noRowsVariant: "skeleton",
                  },
                }}
                initialState={{
  density: "compact",
  pagination: {
    paginationModel: { pageSize: 20 },
  },
}}
pageSizeOptions={[5, 10, 15, 20]}
                rows={patients}
                columns={patientColumns}
                pageSize={20}
                rowsPerPageOptions={[5, 10, 15, 20]}
                disableSelectionOnClick
                disableColumnMenu
                sx={{
                  width: "100%",
                  "& .sticky-column": {
                    position: "sticky",
                    right: 0,
                    backgroundColor: "white",
                    zIndex: 100,
                  },
                  "& .MuiTablePagination-toolbar": {
                    minHeight: "unset",
                    alignItems: "center",
                    paddingTop: "0 !important",
                    paddingBottom: "0 !important",
                  },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                    {
                      margin: 0,
                    },
                }}
              />
            </Grid2>
          )}
          <Menu
  anchorEl={menuAnchor}
  open={Boolean(menuAnchor)}
  onClose={handleMenuClose}
>
  {currentPatient && (
    <MenuItem
      onClick={() => {
        onEditPatient(currentPatient);
        handleMenuClose();
      }}
    >
      <ListItemIcon>
        <Edit fontSize="small" />
      </ListItemIcon>
      Edit Patient
    </MenuItem>
  )}
</Menu>
        </Grid2>

        <PatientSearchDialog
          key="fromPatientList"
          setShowSearchPanel={setShowSearchPanel}
          showSearchPanel={showSearchPanel}
          onViewDetails={onViewDetails}
        />
      </>
    );
  }
);

export default PatientList;